import React from 'react';
import ErrorBoundary from '../components/ErrorBoundary';

/**
 * Terms of Service Page
 * Single Responsibility: Display terms of service
 */
const TermsOfService = () => {
  return (
    <ErrorBoundary section="Terms of Service">
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow p-8 prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700">
                By accessing and using eloNest Financial Management ("the Service"), you accept and agree to be bound by the terms 
                and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                eloNest provides a cloud-based financial management platform that enables users to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Track income and expenses</li>
                <li>Manage budgets and financial planning</li>
                <li>Create and send invoices</li>
                <li>Generate financial reports</li>
                <li>Manage multiple businesses</li>
                <li>Import and reconcile bank transactions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700 mb-4">
                To use the Service, you must create an account by providing accurate, current, and complete information. 
                You are responsible for maintaining the confidentiality of your account credentials.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Security</h3>
              <p className="text-gray-700 mb-4">
                You are responsible for all activities that occur under your account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Keep your password secure and confidential</li>
                <li>Notify us immediately of any unauthorized use</li>
                <li>Use a strong, unique password</li>
                <li>Not share your account with others</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Use the Service for any illegal purpose or in violation of any laws</li>
                <li>Attempt to gain unauthorized access to the Service or related systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Transmit any viruses, malware, or harmful code</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Resell or redistribute the Service without authorization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Subscription and Billing</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Subscription Plans</h3>
              <p className="text-gray-700 mb-4">
                We offer various subscription plans with different features and pricing. Details of current plans are available 
                on our website. We reserve the right to modify subscription plans and pricing with 30 days' notice.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Billing</h3>
              <p className="text-gray-700 mb-4">
                Subscriptions are billed in advance on a monthly or annual basis. By subscribing, you authorize us to charge 
                your payment method automatically.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Cancellation</h3>
              <p className="text-gray-700">
                You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. 
                No refunds are provided for partial billing periods.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Content</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Your Data</h3>
              <p className="text-gray-700 mb-4">
                You retain all ownership rights to your data. By using the Service, you grant us a license to use, store, 
                and process your data solely for the purpose of providing the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Data Accuracy</h3>
              <p className="text-gray-700">
                You are responsible for the accuracy and completeness of all data you enter into the Service. We are not 
                responsible for errors resulting from inaccurate or incomplete data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
              <p className="text-gray-700">
                The Service, including its original content, features, and functionality, is owned by eloNest and protected by 
                international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not 
                copy, modify, distribute, sell, or lease any part of the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimer of Warranties</h2>
              <p className="text-gray-700 mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
                INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
                <li>That the Service will be uninterrupted, secure, or error-free</li>
                <li>That defects will be corrected</li>
                <li>That the Service is free of viruses or other harmful components</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
              <p className="text-gray-700">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ELONEST SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, 
                OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify and hold harmless eloNest, its officers, directors, employees, and agents from any claims, 
                damages, losses, liabilities, and expenses (including legal fees) arising out of or relating to your use of the 
                Service, violation of these Terms, or infringement of any rights of another.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, 
                including breach of these Terms. Upon termination:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Your right to use the Service will immediately cease</li>
                <li>You may request export of your data within 30 days</li>
                <li>We may delete your account and data after the retention period</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of the State of [State], United States, 
                without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the 
                exclusive jurisdiction of the courts in [State].
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify these Terms at any time. We will notify users of material changes via email or 
                through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                <p><strong>Email:</strong> <a href="mailto:legal@elonest.com" className="text-blue-600 hover:text-blue-700">legal@elonest.com</a></p>
                <p><strong>Address:</strong> eloNest Financial Management, 123 Business Street, Suite 100, City, State 12345, United States</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default TermsOfService;

